import { Dispatch, SetStateAction, useCallback, useEffect, useMemo } from 'react'

import { useConfigsContext } from '@/context/ConfigsContext'
import { extractIdFromImageUrl } from '@/lib/asset'
import { getFile, getVod } from '@/services/blendvision'
import { TrailerMedia, updateTrailerMedia } from '@/services/cms'
import { Asset } from '@/types/asset'

export const useFetchTrailerBlendVisionVodInfo = (
  assets: Asset[],
  setAssets: Dispatch<SetStateAction<Asset[]>>,
  roomId?: string,
  defaultAssets?: TrailerMedia,
) => {
  const { store } = useConfigsContext()

  const htcToken = store?.credentials?.htcToken

  const noUrlVodAssets = useMemo(
    () =>
      assets.filter(
        (asset) =>
          asset.video &&
          asset.video.vodId &&
          !asset.video.isConverted &&
          !asset.video.isFetching &&
          !asset.video.url,
      ),
    [assets],
  )

  const handleFetchTrailerBlendVisionVodInfo = useCallback(async () => {
    if (noUrlVodAssets.length == 0) return
    const promises = noUrlVodAssets.map(async (asset) => {
      setAssets((prev) =>
        prev.map((a) =>
          a.video && a.video?.vodId === asset.video?.vodId
            ? { ...a, video: { ...a.video, isFetching: true } }
            : a,
        ),
      )

      const vodResponse = await getVod(asset.video!.vodId)
      if ('vod' in vodResponse) {
        const vod = vodResponse.vod
        const fileId = vod.source.library.video.id
        const coverImage = vod.cover_image?.auto.url
        const isConverted = vod.status === 'VOD_STATUS_SUCCEEDED'
        const { download_uri } = await getFile(fileId)
        setAssets((prev) =>
          prev.map((a) =>
            a.video && a.video?.vodId === asset.video?.vodId
              ? {
                  ...a,
                  video: {
                    ...a.video,
                    ...(coverImage && { coverImage }),
                    url: download_uri,
                    fileId,
                    isFetching: false,
                    isConverted,
                  },
                }
              : a,
          ),
        )
      } else if (htcToken && roomId) {
        // 🥷🏼 handle unhandled error if vod error
        const trailerMedia = {
          trailer_videos: defaultAssets?.trailer_videos?.filter(
            (a) => a.vod_id !== asset.video?.vodId,
          ),
          trailer_images: defaultAssets?.trailer_images?.map((image) => {
            return { id: extractIdFromImageUrl(image.url) }
          }),
        }
        updateTrailerMedia(htcToken, roomId, trailerMedia).then(() => {
          setAssets((prev) => prev.filter((a) => a.video?.vodId !== asset.video?.vodId))
        })
      }
    })

    await Promise.all(promises)
  }, [noUrlVodAssets, setAssets, defaultAssets, htcToken, roomId])

  useEffect(() => {
    handleFetchTrailerBlendVisionVodInfo()
  }, [handleFetchTrailerBlendVisionVodInfo])
}
